import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FarmerQuestionnaireActions, KYCActions, selectKycAppState } from '@redux';
import breakpointSizes from '@applicationKYC/LegalEntityQuestionnaire/styled-theme-styles/global-style-variables.style';
import useWindowDimensions from '@applicationKYC/LegalEntityQuestionnaire/Utils/window-size.service';
import {
  Container,
  QuestionnaireWrapper,
  StepperWrapper
} from '@applicationKYC/LegalEntityQuestionnaire/screens/renderPage/renderPage.style';
import { Loader, Stepper } from '@single-platform/components';
import { RefreshToken } from '@utilities/refreshToken';
import { FarmAddressPage, FarmInfoPage, FarmActivityPage } from '@farmSection/pages';
import { farmSubmitHandler } from '@applicationKYC/FarmersQuestionnaire/farmersQuestionnaire';
import { storageHandler } from '@storageHandler';

const FarmerInitialStepOptions = [
  {
    label: 'farmInfoPage',
    completionIndex: 0,
    previousActiveStep: 0,
    isCompleted: false,
    isActive: false
  },
  {
    label: 'farmAddressPage',
    completionIndex: 1,
    previousActiveStep: 0,
    isCompleted: false,
    isActive: false
  },
  {
    label: 'farmActivityPage',
    completionIndex: 2,
    previousActiveStep: 0,
    isCompleted: false,
    isActive: false
  }
];

export const FarmSection = ({ handleSuccessCallback }) => {
  const { width } = useWindowDimensions();
  const { farmerInformation, activeStep: prevActiveStep, loading } = useSelector(
    (state) => state.FarmersQuestionnaireReducer
  );
  const applicationState = useSelector(selectKycAppState);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState(FarmerInitialStepOptions);
  const questionnaireId = storageHandler.getSessionStorage('questionnaireId');
  const dispatch = useDispatch();

  const handleHeight = () => {
    setIsAtBottom(window.innerHeight + window.scrollY >= document.body.offsetHeight - 20);
  };

  const markStepAsCompleted = (stepIndex) => {
    setSteps((prevSteps) =>
      prevSteps.map((step, index) => (index === stepIndex ? { ...step, isCompleted: true } : step))
    );
  };

  const handleStepChange = async () => {
    const nextStep = activeStep + 1;
    if (nextStep < steps.length) {
      markStepAsCompleted(activeStep);
      setActiveStep(nextStep);
    }
  };

  const handleStepBack = async (step) => {
    const previousStep = step - 1;
    if (previousStep >= 0) {
      setActiveStep(previousStep);
    }
  };

  const handleSubmit = async (data, page) => {
    if (applicationState?.state === 'UserFillingApplication') {
      farmerInformation.farmSection[page] = data;
      await farmSubmitHandler(
        farmerInformation,
        farmerInformation.farmSection,
        dispatch,
        questionnaireId,
        'farmSection'
      );
    }
    handleStepChange();
  };

  useEffect(() => {
    dispatch(KYCActions.getApplication());
  }, []);

  useEffect(() => {
    handleHeight();
    dispatch(FarmerQuestionnaireActions.setFarmerQuestionnaireSession(true));
    dispatch(RefreshToken());
    if (!farmerInformation && questionnaireId)
      dispatch(FarmerQuestionnaireActions.getFarmerInformation(questionnaireId));
    dispatch(FarmerQuestionnaireActions.setStepBackQuestionnaireHandler(handleStepBack));
    document.addEventListener('scroll', handleHeight);
    return () => document.removeEventListener('scroll', handleHeight);
  }, []);

  useEffect(() => {
    if (questionnaireId) dispatch(FarmerQuestionnaireActions.getFarmerInformation(questionnaireId));
  }, [questionnaireId]);

  useEffect(() => {
    setSteps((prevSteps) => {
      const updatedSteps = prevSteps.map((step) => {
        if (farmerInformation?.farmSection[step.label]?.isValid) {
          return { ...step, isCompleted: true };
        }
        return step;
      });

      if (updatedSteps.every((step) => step.isCompleted && !prevActiveStep)) {
        setActiveStep(0);
      } else {
        const firstIncompleteIndex = updatedSteps.findIndex((step) => !step.isCompleted);
        setActiveStep(firstIncompleteIndex === -1 ? prevActiveStep : firstIncompleteIndex);
      }

      return updatedSteps;
    });
  }, [farmerInformation]);

  useEffect(() => {
    dispatch(FarmerQuestionnaireActions.setQuestionnaireActiveStep(activeStep));
    setSteps((prevSteps) => {
      const updatedSteps = prevSteps.map((step, index) => {
        return { ...step, isActive: index === activeStep, previousActiveStep: activeStep - 1 };
      });
      return updatedSteps;
    });
  }, [activeStep]);

  if (loading || !farmerInformation) {
    return <Loader />;
  }

  return (
    <Container
      small={width < breakpointSizes.phone}
      medium={width < breakpointSizes.smallDesktop}
      isLastPage={false}
    >
      <StepperWrapper>
        <Stepper switchToAlternateViewFrom={breakpointSizes.desktopBig} steps={steps} />
      </StepperWrapper>

      <QuestionnaireWrapper
        small={width < breakpointSizes.phone}
        medium={width < breakpointSizes.smallDesktop}
      >
        {activeStep === 0 && (
          <FarmInfoPage
            onSubmit={handleSubmit}
            isAtBottom={isAtBottom}
            handleStepBack={handleStepBack}
          />
        )}
        {activeStep === 1 && (
          <FarmAddressPage
            onSubmit={handleSubmit}
            isAtBottom={isAtBottom}
            handleStepBack={handleStepBack}
          />
        )}
        {activeStep === 2 && (
          <FarmActivityPage
            onSubmit={handleSubmit}
            isAtBottom={isAtBottom}
            handleStepBack={handleStepBack}
            handleSuccessCallback={handleSuccessCallback}
          />
        )}
      </QuestionnaireWrapper>
    </Container>
  );
};
